import { distinctUntilChanged, map, pluck } from 'rxjs/operators'
import { DealConfigurationQuery } from '../../../../../generated/graphql'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { DealQueryService } from 'modules/deals/services/dealQuery.service'
import { DealOption } from 'modules/deals/utilities/deal-types'

/**
 * available configuration option categories
 */
export enum ConfigurationOptionCategory {
    configurator = 'configurator',
    contract = 'contract',
    calibrate = 'calibrate',
    global = 'global', // used by globally set able options
}

/**
 * Configuration options per category
 */
export type ConfigurationOptionsCategoryMap = { [k in ConfigurationOptionCategory]: DealOption[] }

/**
 * Puts named options in a category if category is not set explicitly trough api
 * Also overrides the order displayed in
 */
export const categoryOptionNames = {
    [ConfigurationOptionCategory.configurator]: [
        'edition',
        'paint',
        'rims',
        'steeringWheel',
        'interior',
        'seating',
        'autopilot',
        'towhitch',
    ],
    [ConfigurationOptionCategory.contract]: ['mileage', 'duration'],
    [ConfigurationOptionCategory.calibrate]: ['downpayment', 'deductible', 'winterTires', 'premiumConnectivity'],
    [ConfigurationOptionCategory.global]: ['leasingType'],
}

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class ConfigurationCategoryService {
    constructor(private dealQueryService: DealQueryService) {}

    /**
     * Get all configurationOptions and arrange into object ordered per category
     * type
     * @param configurationId
     */
    getConfigurationOptions(id: string): Observable<ConfigurationOptionsCategoryMap | {}> {
        return (
            // get all configuration options
            this.dealQueryService.watchDealConfiguration(id).pipe(
                untilDestroyed(this),
                pluck<DealConfigurationQuery['Deal'], DealOption[]>('options'),

                // prevent double updates
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),

                // arrange into ConfigurationOptionsCategory map
                map(configurationOptions => {
                    // create an empty category map
                    const emptyCategoryMap = Object.values(ConfigurationOptionCategory).reduce(
                        (emptyMap, category) => ({ ...emptyMap, [category]: [] }),
                        {}
                    )

                    const optionPerCategory = (configurationOptions || []).reduce((categories, option) => {
                        categories[this.getOptionCategory(option)].push(option)
                        return categories
                    }, emptyCategoryMap)

                    // put into predefined order
                    Object.keys(optionPerCategory).forEach(category => {
                        optionPerCategory[category] = [...optionPerCategory[category]].sort((optionA, optionB) => {
                            const optionKeyA = optionA.__typename === 'BooleanDealOption' ? optionA.key : optionA.name
                            const optionKeyB = optionB.__typename === 'BooleanDealOption' ? optionB.key : optionB.name

                            return (
                                (categoryOptionNames[category].indexOf(optionKeyA) + 1 || 500) -
                                (categoryOptionNames[category].indexOf(optionKeyB) + 1 || 500)
                            )
                        })
                    })

                    return optionPerCategory
                }),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
            )
        )
    }

    /**
     * get all options bound to configuration
     * @param configurationId
     * @param category
     */
    getConfigurationCategoryOptions(
        configurationId: string,
        category: ConfigurationOptionCategory | ConfigurationOptionCategory[]
    ): Observable<DealOption[]> {
        const categories: ConfigurationOptionCategory[] = Array.isArray(category) ? category : [category]
        return this.getConfigurationOptions(configurationId).pipe(
            untilDestroyed(this),
            map<ConfigurationOptionsCategoryMap, DealOption[]>(categoryOptions =>
                categories.reduce((result, category) => result.concat(categoryOptions[category]), [])
            ),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
    }

    /**
     * Get the available categories for configuration options
     *
     * @param configurationId
     */
    getConfigurationOptionCategories(configurationId: string): Observable<ConfigurationOptionCategory[]> {
        return this.getConfigurationOptions(configurationId).pipe(
            untilDestroyed(this),
            map(categories => {
                return (
                    Object.values(ConfigurationOptionCategory)

                        // remove empty categories
                        .filter(category => categories.hasOwnProperty(category) && categories[category].length)
                )
            }),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
    }

    /**
     * gets the category a given option belongs to
     *
     * @param option
     */
    private getOptionCategory(option: DealOption): ConfigurationOptionCategory {
        let result = ConfigurationOptionCategory.configurator

        const optionKey = option.__typename === 'BooleanDealOption' ? option.key : option.name

        // get category has been set explicitly by api, use if available locally

        if (categoryOptionNames[ConfigurationOptionCategory.configurator].includes(optionKey)) {
            result = ConfigurationOptionCategory.configurator
        } else if (categoryOptionNames[ConfigurationOptionCategory.contract].includes(optionKey)) {
            result = ConfigurationOptionCategory.contract
        } else if (categoryOptionNames[ConfigurationOptionCategory.calibrate].includes(optionKey)) {
            result = ConfigurationOptionCategory.calibrate
        } else if (categoryOptionNames[ConfigurationOptionCategory.global].includes(optionKey)) {
            result = ConfigurationOptionCategory.global
        }

        return result
    }
}
